/**
 * Histórico de execuções.
 *
 * Tabela com filtro e ordenação. Execuções marcadas como não comparáveis
 * carregam o aviso na própria linha — quem lê a tabela precisa perceber a
 * ressalva **antes** de olhar o número, e não depois.
 */

import { useMemo, useState } from "react";
import { Badge, Tabs, Tooltip, type TabsItem } from "@cloudflare/kumo";
import { useNavigate } from "react-router-dom";
import { DataTable, type Column } from "../components/DataTable.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { VerdictBadge } from "../components/VerdictBadge.js";
import { snapshot } from "../data/source.js";
import type { Verdict } from "../fixtures/index.js";
import { formatContext, formatNumber } from "../lib/format.js";

/** Uma linha da tabela: o cenário com o contexto da execução a que pertence. */
interface Row {
  readonly scenarioId: number;
  readonly runId: number;
  readonly instanceType: string;
  readonly gpu: string;
  readonly engine: string;
  readonly loadProfile: string;
  readonly concurrency: number;
  readonly contextLen: number;
  readonly tokensPerSec: number;
  readonly ttftP95: number;
  readonly goodput: number;
  readonly verdict: Verdict;
  readonly comparable: boolean;
  readonly notComparableReason: string | null;
}

/** Valor do filtro que deixa passar tudo. */
const ALL = "all";

/** Linhas por página: dez cabem sem rolar num notebook de 13 polegadas. */
const PAGE_SIZE = 10;

/** Filtro por perfil de carga. */
const PROFILE_TABS: TabsItem[] = [
  { value: ALL, label: "todos" },
  { value: "chat", label: "chat" },
  { value: "document", label: "documento" },
  { value: "mixed", label: "misto" },
];

/** Filtro por motor de inferência. */
const ENGINE_TABS: TabsItem[] = [
  { value: ALL, label: "todos" },
  { value: "sglang", label: "sglang" },
  { value: "sglang+dynamo", label: "sglang+dynamo" },
];

/** Rótulo pt-BR de cada perfil de carga. */
const PROFILE_LABEL: Readonly<Record<string, string>> = {
  chat: "chat",
  document: "documento",
  mixed: "misto",
};

/** Colunas da tabela; latência sempre em percentil. */
const COLUMNS: readonly Column<Row>[] = [
  { key: "scenarioId", title: "#", sortValue: (row) => row.scenarioId, render: (row) => row.scenarioId },
  {
    key: "instanceType",
    title: "instância",
    sortValue: (row) => row.instanceType,
    render: (row) =>
      row.comparable ? (
        row.instanceType
      ) : (
        <Tooltip content={row.notComparableReason ?? ""} render={<span className="inline-flex items-center gap-2" />}>
          <Badge variant="warning">não comparável</Badge>
          {row.instanceType}
        </Tooltip>
      ),
  },
  // O desfecho vem logo depois da identificação, e não no fim: em tela de notebook a
  // tabela rola na horizontal, e a coluna que decide ficava escondida (medido em 15/09/2026).
  { key: "verdict", title: "desfecho", render: (row) => <VerdictBadge verdict={row.verdict} /> },
  { key: "gpu", title: "aceleradores", render: (row) => <span className="whitespace-nowrap">{row.gpu}</span> },
  { key: "engine", title: "motor", render: (row) => row.engine },
  { key: "loadProfile", title: "perfil", render: (row) => PROFILE_LABEL[row.loadProfile] ?? row.loadProfile },
  {
    key: "concurrency",
    title: "concorrência",
    align: "right",
    sortValue: (row) => row.concurrency,
    render: (row) => row.concurrency,
  },
  {
    key: "contextLen",
    title: "contexto",
    align: "right",
    sortValue: (row) => row.contextLen,
    render: (row) => formatContext(row.contextLen),
  },
  {
    key: "tokensPerSec",
    title: "tok/s (p95)",
    align: "right",
    sortValue: (row) => row.tokensPerSec,
    render: (row) => <span className="font-semibold text-kumo-strong">{formatNumber(row.tokensPerSec, 1)}</span>,
  },
  {
    key: "ttftP95",
    title: "TTFT (p95)",
    align: "right",
    sortValue: (row) => row.ttftP95,
    render: (row) => <span className="whitespace-nowrap">{formatNumber(row.ttftP95)} ms</span>,
  },
  {
    key: "goodput",
    title: "goodput",
    align: "right",
    sortValue: (row) => row.goodput,
    render: (row) => `${formatNumber(row.goodput, 1)}%`,
  },
  { key: "verdict", title: "desfecho", render: (row) => <VerdictBadge verdict={row.verdict} /> },
];

/** Histórico navegável de execuções e cenários. */
export function Runs(): React.ReactElement {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(ALL);
  const [engine, setEngine] = useState(ALL);

  const rows = useMemo<Row[]>(
    () =>
      snapshot.scenarios.map((scenario) => {
        const run = snapshot.runs.find((r) => r.id === scenario.runId);
        const stats = snapshot.stats.find((s) => s.scenarioId === scenario.id);
        return {
          scenarioId: scenario.id,
          runId: scenario.runId,
          instanceType: run?.instanceType ?? "—",
          gpu: `${run?.gpuCount ?? 0}× ${run?.gpuModel ?? "—"}`,
          engine: run?.engine ?? "—",
          loadProfile: scenario.loadProfile,
          concurrency: scenario.concurrency,
          contextLen: scenario.contextLen,
          tokensPerSec: stats?.tokensPerSecP95 ?? 0,
          ttftP95: stats?.ttftP95Ms ?? 0,
          goodput: stats?.goodputPct ?? 0,
          verdict: stats?.verdict ?? "fails",
          comparable: run?.comparable ?? true,
          notComparableReason: run?.notComparableReason ?? null,
        };
      }),
    [],
  );

  const visible = rows.filter(
    (row) => (profile === ALL || row.loadProfile === profile) && (engine === ALL || row.engine === engine),
  );

  return (
    <Page>
      <Panel
        title="Execuções e cenários"
        description="Todas as colunas de latência são percentis. Média não aparece em lugar nenhum — ela esconde a cauda que reprova o alvo."
        bodyClassName="flex flex-col gap-4"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-kumo-subtle">perfil</span>
            <Tabs variant="segmented" size="sm" tabs={PROFILE_TABS} value={profile} onValueChange={setProfile} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-kumo-subtle">motor</span>
            <Tabs variant="segmented" size="sm" tabs={ENGINE_TABS} value={engine} onValueChange={setEngine} />
          </div>
          <span className="text-sm text-kumo-subtle">
            {visible.length} de {rows.length} cenários · clique numa linha para o detalhe
          </span>
        </div>
        {/* A chave remonta a tabela ao trocar o filtro: sem isso a página corrente
            sobreviveria e poderia apontar para além do fim da lista filtrada. */}
        <DataTable
          key={`${profile}|${engine}`}
          columns={COLUMNS}
          rows={visible}
          rowKey={(row) => row.scenarioId}
          onRowClick={(row) => navigate(`/scenario/${row.scenarioId}`)}
          rowClassName={(row) => (row.comparable ? "" : "opacity-60")}
          pageSize={PAGE_SIZE}
          emptyText="nenhum cenário com esse filtro"
        />
      </Panel>
    </Page>
  );
}
